import { useState, useCallback } from 'react'
import Drawer from '@/components/drawer/drawer'
import { getPolicyByType } from '@/lib/api/policies/policies-api'
import { PolicyType } from '@/lib/api/common/interfaces/policy.interface'
import { toast } from 'react-toastify'

type DocumentType = 'terms' | 'privacy'

interface TermsAgreementProps {
    checked: boolean
    onChange: (checked: boolean) => void
    className?: string
}

const DOCUMENT_CONFIG: Record<DocumentType, { title: string; policyType: PolicyType }> = {
    terms: {
        title: 'Termos de Uso',
        policyType: PolicyType.TERMS_OF_SERVICE,
    },
    privacy: {
        title: 'Política de Privacidade',
        policyType: PolicyType.PRIVACY_POLICY,
    },
}

export default function TermsAgreement({
    checked,
    onChange,
    className = '',
}: TermsAgreementProps) {
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [drawerTitle, setDrawerTitle] = useState('')
    const [policyContent, setPolicyContent] = useState('')
    const [loading, setLoading] = useState(false)

    const openDocument = useCallback(
        async (type: DocumentType, e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()

            const config = DOCUMENT_CONFIG[type]
            setDrawerTitle(config.title)
            setDrawerOpen(true)
            setLoading(true)

            try {
                const response = await getPolicyByType(config.policyType)

                if (response.success && response.result) {
                    setPolicyContent(response.result.content)
                } else {
                    toast.error('Documento não encontrado')
                    setPolicyContent('')
                }
            } catch {
                toast.error('Erro ao carregar documento')
                setPolicyContent('')
            } finally {
                setLoading(false)
            }
        },
        []
    )

    return (
        <>
            <label
                className={`flex items-start gap-3 cursor-pointer p-4 rounded-xl hover:bg-base-100 transition-colors ${className}`}
            >
                <input
                    type="checkbox"
                    className="checkbox checkbox-secondary mt-1"
                    checked={checked}
                    onChange={e => onChange(e.target.checked)}
                />
                <span className="text-sm text-gray-600">
                    Li e concordo com os{' '}
                    <button
                        type="button"
                        onClick={e => openDocument('terms', e)}
                        className="text-secondary hover:underline font-semibold"
                    >
                        termos de uso
                    </button>{' '}
                    e{' '}
                    <button
                        type="button"
                        onClick={e => openDocument('privacy', e)}
                        className="text-secondary hover:underline font-semibold"
                    >
                        política de privacidade
                    </button>
                </span>
            </label>

            <Drawer
                isOpen={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={drawerTitle}
                width="w-full sm:w-[500px] md:w-[600px] lg:w-[700px]"
            >
                <div className="p-6">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary"></div>
                        </div>
                    ) : (
                        <div
                            className="prose prose-sm sm:prose-base lg:prose-lg max-w-none"
                            style={{ lineHeight: '1.6' }}
                            dangerouslySetInnerHTML={{ __html: policyContent }}
                        />
                    )}
                </div>
            </Drawer>
        </>
    )
}
